// Routing table and access guards of the advisor workstation.
// Every screen module is lazy loaded: the login screen and the shell are the only
// code paths on the critical rendering path.
import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import type { RoleUtilisateur } from '@horizon/types';
import { useSessionStore } from '@/stores/session';

declare module 'vue-router' {
  interface RouteMeta {
    /** French document title, appended to the product name. */
    titre: string;
    /** Reachable without an authenticated session. */
    public?: boolean;
    /** Screen that mutates data: forbidden to the read-only roles. */
    ecriture?: boolean;
    /** Explicit allow list; when absent every authenticated role may enter. */
    roles?: readonly RoleUtilisateur[];
  }
}

const LECTURE_ET_ECRITURE: readonly RoleUtilisateur[] = ['conseiller', 'administrateur'];
const DOSSIERS_NOMINATIFS: readonly RoleUtilisateur[] = [
  'conseiller',
  'administrateur',
  'conformite',
];

const routes: readonly RouteRecordRaw[] = [
  {
    path: '/connexion',
    name: 'connexion',
    component: () => import('@/modules/connexion/PageConnexion.vue'),
    meta: { titre: 'Connexion', public: true },
  },
  {
    path: '/',
    name: 'tableau-de-bord',
    component: () => import('@/modules/tableau-de-bord/PageTableauDeBord.vue'),
    meta: { titre: 'Tableau de bord' },
  },
  {
    path: '/clients',
    name: 'clients',
    component: () => import('@/modules/clients/PageClients.vue'),
    meta: { titre: 'Clients', roles: DOSSIERS_NOMINATIFS },
  },
  {
    path: '/clients/:id',
    name: 'client',
    component: () => import('@/modules/clients/PageClient.vue'),
    props: true,
    meta: { titre: 'Fiche client', roles: DOSSIERS_NOMINATIFS },
  },
  {
    path: '/simulateur/:clientId?',
    name: 'simulateur',
    component: () => import('@/modules/simulateur/PageSimulateur.vue'),
    props: true,
    meta: { titre: 'Simulateur épargne', ecriture: true, roles: LECTURE_ET_ECRITURE },
  },
  {
    path: '/portefeuilles',
    name: 'portefeuilles',
    component: () => import('@/modules/portefeuilles/PagePortefeuilles.vue'),
    meta: { titre: 'Portefeuilles' },
  },
  {
    path: '/documents',
    name: 'documents',
    component: () => import('@/modules/documents/PageDocuments.vue'),
    meta: { titre: 'Documents', roles: DOSSIERS_NOMINATIFS },
  },
  {
    // Open to every authenticated role, as in the reference prototype: the KYC queue is
    // visible to all, and the screen itself gates the audit journal (administrateur and
    // conformité only), backed server side by the audit_select RLS policy.
    path: '/conformite',
    name: 'conformite',
    component: () => import('@/modules/conformite/PageConformite.vue'),
    meta: { titre: 'Conformité' },
  },
  {
    path: '/parametres',
    name: 'parametres',
    component: () => import('@/modules/parametres/PageParametres.vue'),
    // The reference screen shows Paramètres to the advisor (profile, hypotheses,
    // security); write actions inside are gated per section, not at the route.
    meta: { titre: 'Paramètres' },
  },
  {
    path: '/:chemin(.*)*',
    name: 'introuvable',
    redirect: { name: 'tableau-de-bord' },
    meta: { titre: 'Page introuvable' },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [...routes],
  scrollBehavior(_versLaRoute, _depuisLaRoute, position) {
    return position ?? { top: 0 };
  },
});

router.beforeEach(async (versLaRoute) => {
  const session = useSessionStore();
  await session.initialiser();

  if (versLaRoute.meta.public === true) {
    return session.estAuthentifie ? { name: 'tableau-de-bord' } : true;
  }

  if (!session.estAuthentifie) {
    return { name: 'connexion', query: { suite: versLaRoute.fullPath } };
  }

  // Read-only roles (conformité, direction) never reach a mutating screen.
  if (versLaRoute.meta.ecriture === true && !session.peutEcrire) {
    return { name: 'tableau-de-bord' };
  }

  const rolesAutorises = versLaRoute.meta.roles;
  const role = session.role;
  if (rolesAutorises !== undefined && (role === null || !rolesAutorises.includes(role))) {
    return { name: 'tableau-de-bord' };
  }

  return true;
});

router.afterEach((versLaRoute) => {
  const titre = versLaRoute.meta.titre;
  document.title = typeof titre === 'string' && titre.length > 0 ? `${titre} · HORIZON` : 'HORIZON';
});

export default router;
